import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import MainNavigation from '../../components/ui/MainNavigation';

const vacatures = [
  {
    id: 1,
    titel: "Energieadviseur (parttime/fulltime)",
    type: "Vast",
    locatie: "Den Haag / Hybride",
    omschrijving: "Jij helpt huiseigenaren met onafhankelijk advies over verduurzaming. Je hebt kennis van warmtepompen, isolatie, zonnepanelen en subsidies, en weet dit helder te communiceren.",
    eisen: ["BRL 9500 certificering of bereidheid dit te behalen", "Minimaal 2 jaar relevante werkervaring", "Uitstekende communicatieve vaardigheden", "Klantgericht en proactief"],
  },
  {
    id: 2,
    titel: "Content Redacteur Duurzaamheid",
    type: "Vast",
    locatie: "Den Haag / Remote",
    omschrijving: "Jij schrijft heldere, betrouwbare en SEO-geoptimaliseerde content over woningverduurzaming. Je maakt complexe technische informatie toegankelijk voor een breed publiek.",
    eisen: ["HBO/WO werk- en denkniveau", "Aantoonbare schrijfervaring", "Affiniteit met duurzaamheid en energie", "Kennis van SEO is een pré"],
  },
  {
    id: 3,
    titel: "Business Development Manager",
    type: "Vast",
    locatie: "Den Haag",
    omschrijving: "Jij bouwt en onderhoudt relaties met installateurs, leveranciers en andere partners. Je bent verantwoordelijk voor het uitbreiden van ons aanbiedernetwerk.",
    eisen: ["HBO/WO werk- en denkniveau", "Minimaal 3 jaar B2B saleservaring", "Netwerk in de duurzaamheidssector is een pré", "Resultaatgericht en ondernemend"],
  },
];

const sections = [
  {
    id: 1,
    title: "Werken bij Verduurzaamwijzer.nu",
    content: (
      <div className="space-y-4">
        <p>
          Bij Verduurzaamwijzer.nu werk je aan iets wat er echt toe doet: het helpen van huiseigenaren bij het
          verduurzamen van hun woning. Wij zijn een groeiend platform met een hecht team van energieadviseurs,
          redacteuren, ontwikkelaars en commerciële professionals.
        </p>
        <p>
          Wij geloven in een platte organisatie, veel eigen verantwoordelijkheid en de vrijheid om je werk op
          jouw manier te doen — zolang het resultaat er is. Hybride werken is de norm, niet de uitzondering.
        </p>
      </div>
    ),
  },
  {
    id: 2,
    title: "Wat wij bieden",
    content: (
      <div className="space-y-3">
        <ul className="space-y-3 list-none">
          {[
            ["Marktconform salaris", "Wij betalen eerlijk en transparant, passend bij jouw ervaring en rol."],
            ["Hybride werken", "Combineer kantoor (Den Haag) met thuiswerken op een manier die bij jou past."],
            ["Persoonlijke ontwikkeling", "Budget voor opleidingen, cursussen en conferenties."],
            ["Pensioenregeling", "Goede pensioenopbouw via ons collectieve pensioenplan."],
            ["Duurzame impact", "Jouw werk draagt direct bij aan de verduurzaming van Nederland."],
            ["Informele cultuur", "Geen onnodige hiërarchie, wel veel ruimte voor initiatief."],
          ]?.map(([label, desc], i) => (
            <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
              <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
              <span><strong>{label}:</strong> {desc}</span>
            </li>
          ))}
        </ul>
      </div>
    ),
  },
  {
    id: 3,
    title: "Openstaande vacatures",
    content: (
      <div className="space-y-5">
        {vacatures?.map((v) => (
          <div key={v?.id} className="border border-border/60 rounded-xl p-5 hover:border-primary/40 transition-colors">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <h3 className="font-heading font-semibold text-foreground text-base">{v?.titel}</h3>
              <div className="flex gap-2">
                <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full font-medium">{v?.type}</span>
                <span className="text-xs px-2 py-1 bg-muted text-foreground/60 rounded-full">{v?.locatie}</span>
              </div>
            </div>
            <p className="text-sm text-foreground/70 mb-3">{v?.omschrijving}</p>
            <div>
              <p className="text-xs font-semibold text-foreground/60 uppercase tracking-wide mb-2">Wij vragen:</p>
              <ul className="space-y-1">
                {v?.eisen?.map((eis, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/70">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                    {eis}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-4 pt-3 border-t border-border/40">
              <a
                href={`mailto:contact@verduurzaamwijzer.nu?subject=Sollicitatie: ${v?.titel}`}
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
              >
                <Icon name="Send" size={14} />
                Solliciteer via e-mail
              </a>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 4,
    title: "Open sollicitatie",
    content: (
      <div className="space-y-4">
        <p>
          Staat uw droomfunctie er niet bij? Wij ontvangen altijd graag een open sollicitatie van gemotiveerde
          kandidaten met een passie voor duurzaamheid.
        </p>
        <p>
          Stuur uw cv en motivatiebrief naar{' '}
          <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
            contact@verduurzaamwijzer.nu
          </a>{' '}
          met als onderwerp "Open sollicitatie". Wij reageren binnen 5 werkdagen.
        </p>
      </div>
    ),
  },
];

const CarrierePage = () => {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary/8 via-background to-background pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 text-sm text-foreground/50 mb-4">
              <Link to="/homepage" className="hover:text-primary transition-colors">Home</Link>
              <Icon name="ChevronRight" size={14} />
              <span className="text-foreground/70">Carrière</span>
            </div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                <Icon name="Briefcase" size={20} className="text-primary" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-foreground">Carrière</h1>
            </div>
            <p className="text-foreground/70 text-lg leading-relaxed max-w-2xl">
              Werk mee aan een duurzamer Nederland. Bekijk onze openstaande vacatures of stuur een open sollicitatie.
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-foreground/50">
              <Icon name="MapPin" size={14} />
              <span>Den Haag · Hybride werken mogelijk</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-10">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-foreground/50 mb-3">Inhoud</h2>
                <nav className="space-y-1">
                  {sections?.map((s) => (
                    <a
                      key={s?.id}
                      href={`#section-${s?.id}`}
                      className="block text-sm text-foreground/60 hover:text-primary transition-colors py-0.5 leading-snug"
                    >
                      {s?.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            <main className="flex-1 min-w-0">
              <div className="space-y-10">
                {sections?.map((section, index) => (
                  <motion.div
                    key={section?.id}
                    id={`section-${section?.id}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.4, delay: index * 0.03 }}
                    className="scroll-mt-24"
                  >
                    <h2 className="font-heading text-lg font-semibold text-foreground mb-3 pb-2 border-b border-border/50">
                      {section?.title}
                    </h2>
                    <div className="text-foreground/75 leading-relaxed text-sm sm:text-base">
                      {section?.content}
                    </div>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name="Mail" size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-1">Interesse? Neem contact op</h3>
                    <p className="text-sm text-foreground/70 mb-3">
                      Stuur uw sollicitatie of vragen naar{' '}
                      <a href="mailto:contact@verduurzaamwijzer.nu" className="text-primary hover:underline font-medium">
                        contact@verduurzaamwijzer.nu
                      </a>{' '}
                      of bel{' '}
                      <a href="tel:0647650537" className="text-primary hover:underline font-medium">
                        06 4765 0537
                      </a>.
                    </p>
                    <p className="text-xs text-foreground/50">
                      De Content Factory BV · Oude Middenweg 17, 2491 AC Den Haag · KvK 84512490
                    </p>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarrierePage;
